import React from 'react';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';
import Pagination from '@mui/material/Pagination';
import CircularProgress from '@mui/material/CircularProgress';
import LinearProgress from '@mui/material/LinearProgress';

const SuspiciousUsersTable = ({ users, loading, onUserClick, onPageChange, currentSkip, limit }) => {
  if (!users || users.length === 0) {
    if (loading) {
      return (
        <Card>
          <Box
            sx={{
              px: 2,
              py: 1.5,
              bgcolor: 'background.paper',
              borderBottom: 1,
              borderColor: 'divider',
            }}
          >
            <Typography variant="subtitle2">Suspicious Users</Typography>
          </Box>
          <CardContent sx={{ textAlign: 'center', py: 5 }}>
            <CircularProgress />
            <Typography sx={{ mt: 2, color: 'text.secondary' }}>
              Loading users...
            </Typography>
          </CardContent>
        </Card>
      );
    }

    return (
      <Card>
        <Box
          sx={{
            px: 2,
            py: 1.5,
            bgcolor: 'background.paper',
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <Typography variant="subtitle2">Suspicious Users</Typography>
        </Box>
        <CardContent sx={{ textAlign: 'center', py: 5 }}>
          <Typography sx={{ color: 'text.secondary', fontSize: '3rem' }}>
            {'\u{1F4E5}'}
          </Typography>
          <Typography sx={{ mt: 2, color: 'text.secondary' }}>
            No users found matching current filters
          </Typography>
        </CardContent>
      </Card>
    );
  }

  const getClassificationChip = (classification) => {
    const config = {
      'HIGH_RISK': { color: 'error', label: 'High Risk' },
      'MEDIUM_RISK': { color: 'warning', label: 'Medium Risk' },
      'LOW_RISK': { color: 'info', label: 'Low Risk' },
      'NO_THREAT': { color: 'success', label: 'No Threat' }
    };
    const c = config[classification] || { color: 'default', label: 'Unknown' };
    return <Chip label={c.label} color={c.color} size="small" />;
  };

  const getSuspicionColor = (score) => {
    if (score >= 80) return 'error';
    if (score >= 60) return 'warning';
    if (score >= 40) return 'info';
    return 'success';
  };

  const currentPage = Math.floor(currentSkip / limit) + 1;
  const hasNextPage = users.length === limit;

  const handlePageChange = (event, page) => {
    const newSkip = (page - 1) * limit;
    onPageChange(newSkip);
  };

  return (
    <Card>
      {/* Header */}
      <Box
        sx={{
          px: 2,
          py: 1.5,
          bgcolor: 'background.paper',
          borderBottom: 1,
          borderColor: 'divider',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Box>
          <Typography variant="subtitle2">Suspicious Users</Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            Showing {currentSkip + 1}-{currentSkip + users.length} users
          </Typography>
        </Box>
      </Box>

      {/* Table */}
      <Box sx={{ maxHeight: 600, overflow: 'auto', position: 'relative' }}>
        {loading && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              bgcolor: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10,
            }}
          >
            <Box sx={{ textAlign: 'center' }}>
              <CircularProgress />
              <Typography sx={{ mt: 2, color: 'text.secondary' }}>
                Refreshing data...
              </Typography>
            </Box>
          </Box>
        )}

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>User</TableCell>
                <TableCell align="center">Classification</TableCell>
                <TableCell align="center">Suspicion Score</TableCell>
                <TableCell align="center">Confidence</TableCell>
                <TableCell align="center">Impossible Travel</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((user) => (
                <TableRow key={user.user_id || user.id} hover>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {user.display_name || user.name || 'Unknown User'}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      {user.user_email || user.email || 'No email'}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    {getClassificationChip(user.classification)}
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ width: 80, mx: 'auto' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LinearProgress
                          variant="determinate"
                          value={user.suspicion_score}
                          color={getSuspicionColor(user.suspicion_score)}
                          sx={{ flexGrow: 1, height: 8, borderRadius: 4 }}
                        />
                        <Typography variant="caption" sx={{ fontWeight: 700, minWidth: 20 }}>
                          {user.suspicion_score}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="caption">
                      {user.confidence
                        ? `${(user.confidence * 100).toFixed(0)}%`
                        : 'N/A'}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    {user.impossible_travel_detected ||
                    user.has_impossible_travel ? (
                      <Chip label="Yes" color="error" size="small" />
                    ) : (
                      <Chip label="None" size="small" />
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => onUserClick(user.user_id || user.id)}
                    >
                      View Details
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Pagination */}
      <Box
        sx={{
          px: 2,
          py: 1,
          bgcolor: 'background.paper',
          borderTop: 1,
          borderColor: 'divider',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          Page {currentPage}
        </Typography>
        <Pagination
          count={hasNextPage ? currentPage + 1 : currentPage}
          page={currentPage}
          onChange={handlePageChange}
          size="small"
          siblingCount={0}
          boundaryCount={1}
        />
      </Box>
    </Card>
  );
};

export default SuspiciousUsersTable;
